import { useQuery } from '@tanstack/react-query';
import type {
  CreateResellerInput,
  CreateResellerPlanInput,
  FeatureDescriptorDto,
  LimitDescriptorDto,
  PlanDescriptorsDto,
  ResellerCeilingReportDto,
  ResellerDto,
  ResellerPlanDto,
  PlanPolicies,
} from '@cpm/shared';
import { RESELLER_OVERSELL_POLICIES } from '@cpm/shared';
import { Eye, Gauge, Network, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ApiClientError, api } from '../api/client.js';
import {
  useCreateReseller,
  useCreateResellerPlan,
  useDeleteReseller,
  useDeleteResellerPlan,
  useResellerPlans,
  useResellerReport,
  useResellers,
  useStartImpersonation,
  useUpdateResellerPlan,
} from '../api/resellers.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Tabs,
} from '../components/ui.js';

const OVERSELL_LABEL: Record<string, string> = {
  strict: 'Strict — no overselling (enforce on allocations)',
  allowed: 'Allowed — oversell, enforce on actual usage',
};

/** One aggregate-ceiling field: a "cap this" toggle plus a number when capped. */
function CeilingField({
  descriptor,
  value,
  onChange,
}: {
  descriptor: LimitDescriptorDto;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  const capped = value !== undefined;
  const label = descriptor.kind === 'megabytes' ? `${descriptor.label} (MB)` : descriptor.label;
  return (
    <div title={descriptor.hint}>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          disabled={!capped}
          value={capped ? value : ''}
          placeholder="No ceiling"
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        />
        <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            className="size-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={capped}
            onChange={(e) => onChange(e.target.checked ? 0 : undefined)}
          />
          cap
        </label>
      </div>
    </div>
  );
}

interface ResellerPlanFormValues {
  name: string;
  description: string;
  maxClients: number | null;
  resourceCeilings: Record<string, number>;
  featureCeilings: Record<string, boolean>;
  policyCeilings: PlanPolicies;
  oversellPolicy: (typeof RESELLER_OVERSELL_POLICIES)[number];
  isActive: boolean;
}

function ResellerPlanForm({
  descriptors,
  initial,
  onDone,
}: {
  descriptors: PlanDescriptorsDto;
  initial?: ResellerPlanDto;
  onDone: () => void;
}) {
  const create = useCreateResellerPlan();
  const update = useUpdateResellerPlan();
  const [values, setValues] = useState<ResellerPlanFormValues>(() => ({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    maxClients: initial?.maxClients ?? null,
    resourceCeilings: (() => {
      const seed: Record<string, number> = {};
      for (const d of descriptors.limits) {
        const v = initial?.resourceCeilings[d.key];
        if (typeof v === 'number') seed[d.key] = v;
      }
      return seed;
    })(),
    featureCeilings: (() => {
      const seed: Record<string, boolean> = {};
      for (const d of descriptors.features) {
        seed[d.key] = initial ? (initial.featureCeilings[d.key] ?? false) : d.default;
      }
      return seed;
    })(),
    policyCeilings: { ...(initial?.policyCeilings ?? {}) },
    oversellPolicy: initial?.oversellPolicy ?? 'strict',
    isActive: initial?.isActive ?? true,
  }));
  const [ackOverage, setAckOverage] = useState<ResellerCeilingReportDto[] | null>(null);

  const payload = (acknowledge: boolean): CreateResellerPlanInput & { acknowledgeOverage?: boolean } => ({
    name: values.name,
    description: values.description || null,
    maxClients: values.maxClients,
    resourceCeilings: values.resourceCeilings,
    featureCeilings: values.featureCeilings,
    policyCeilings: values.policyCeilings,
    oversellPolicy: values.oversellPolicy,
    isActive: values.isActive,
    ...(acknowledge ? { acknowledgeOverage: true } : {}),
  });

  const submit = (acknowledge = false) => {
    if (initial) {
      update.mutate(
        { id: initial.id, input: payload(acknowledge) },
        {
          onSuccess: onDone,
          onError: (err) => {
            if (err instanceof ApiClientError && err.code === 'RESELLER_CEILING_BELOW_USAGE') {
              const offenders = (err.details as { offenders?: { report: ResellerCeilingReportDto }[] })
                ?.offenders;
              setAckOverage(offenders?.map((o) => o.report) ?? []);
            }
          },
        },
      );
    } else {
      create.mutate(payload(false), { onSuccess: onDone });
    }
  };

  const pending = create.isPending || update.isPending;
  const error = (create.error ?? update.error) as unknown;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setAckOverage(null);
        submit(false);
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="rp-name">Name</Label>
        <Input id="rp-name" required value={values.name} onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Max clients</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              disabled={values.maxClients === null}
              value={values.maxClients ?? ''}
              placeholder="Unlimited"
              onChange={(e) => setValues((v) => ({ ...v, maxClients: e.target.value === '' ? 0 : Number(e.target.value) }))}
            />
            <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                className="size-3.5"
                checked={values.maxClients === null}
                onChange={(e) => setValues((v) => ({ ...v, maxClients: e.target.checked ? null : 0 }))}
              />
              ∞
            </label>
          </div>
        </div>
        <div>
          <Label htmlFor="rp-oversell">Oversell policy</Label>
          <Select
            id="rp-oversell"
            value={values.oversellPolicy}
            onChange={(e) => setValues((v) => ({ ...v, oversellPolicy: e.target.value as ResellerPlanFormValues['oversellPolicy'] }))}
          >
            {RESELLER_OVERSELL_POLICIES.map((p) => (
              <option key={p} value={p}>
                {OVERSELL_LABEL[p]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Aggregate resource ceilings</p>
        <div className="grid grid-cols-2 gap-3">
          {descriptors.limits.map((d) => (
            <CeilingField
              key={d.key}
              descriptor={d}
              value={values.resourceCeilings[d.key]}
              onChange={(v) =>
                setValues((s) => {
                  const next = { ...s.resourceCeilings };
                  if (v === undefined) delete next[d.key];
                  else next[d.key] = v;
                  return { ...s, resourceCeilings: next };
                })
              }
            />
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Uncapped keys are unlimited. Disk/memory ceilings are advisory (reported, not blocked at
          creation).
        </p>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Permission ceilings</p>
        <div className="space-y-1.5">
          {descriptors.features.map((d: FeatureDescriptorDto) => (
            <label key={d.key} className="flex items-start gap-2 text-sm" title={d.description}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={values.featureCeilings[d.key] ?? false}
                onChange={(e) =>
                  setValues((s) => ({ ...s, featureCeilings: { ...s.featureCeilings, [d.key]: e.target.checked } }))
                }
              />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          A reseller may only grant its client plans features permitted here (plan ∩ ceiling).
        </p>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Aggregate monthly transfer ceiling
        </p>
        {descriptors.policies
          .filter((descriptor) => descriptor.kind === 'monthly-transfer')
          .map((descriptor) => {
            const current = values.policyCeilings[descriptor.key];
            const transfer =
              current?.kind === 'monthly-transfer' ? current : descriptor.default;
            const capped =
              transfer.kind === 'monthly-transfer' && transfer.allowanceBytes !== null;
            return (
              <div key={descriptor.key} className="flex items-end gap-2">
                <div className="flex-1">
                  <Label>{descriptor.label} (bytes)</Label>
                  <Input
                    type="number"
                    min={0}
                    disabled={!capped}
                    value={
                      transfer.kind === 'monthly-transfer'
                        ? (transfer.allowanceBytes ?? '')
                        : ''
                    }
                    placeholder="No ceiling"
                    onChange={(event) => {
                      if (transfer.kind !== 'monthly-transfer') return;
                      setValues((state) => ({
                        ...state,
                        policyCeilings: {
                          ...state.policyCeilings,
                          [descriptor.key]: {
                            ...transfer,
                            allowanceBytes: Number(event.target.value),
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <label className="mb-2 flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={capped}
                    onChange={(event) =>
                      setValues((state) => {
                        if (transfer.kind !== 'monthly-transfer') return state;
                        const next = { ...state.policyCeilings };
                        if (event.target.checked) {
                          next[descriptor.key] = { ...transfer, allowanceBytes: 0 };
                        } else {
                          delete next[descriptor.key];
                        }
                        return { ...state, policyCeilings: next };
                      })
                    }
                  />
                  cap
                </label>
              </div>
            );
          })}
        <p className="mt-1 text-xs text-slate-500">
          Strict oversell applies this to allocated subscription allowances; allowed oversell
          permits allocations and surfaces actual transfer usage.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={values.isActive} onChange={(e) => setValues((v) => ({ ...v, isActive: e.target.checked }))} />
        Active
      </label>

      {error != null && <ErrorNote error={error} />}

      {ackOverage != null && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/40">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            These ceilings drop below a reseller’s current usage.
          </p>
          {ackOverage.map((report) => (
            <ReportTable key={report.resellerId} report={report} compact />
          ))}
          <div className="mt-2 flex justify-end">
            <Button type="button" variant="danger" loading={pending} onClick={() => submit(true)}>
              Acknowledge & save anyway
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={pending}>
          {initial ? 'Save reseller plan' : 'Create reseller plan'}
        </Button>
      </div>
    </form>
  );
}

function ReportTable({ report, compact }: { report: ResellerCeilingReportDto; compact?: boolean }) {
  return (
    <div className={compact ? 'mt-2' : ''}>
      {!compact && (
        <div className="mb-2 flex flex-wrap gap-2 text-sm">
          <Badge tone={report.maxClientsExceeded ? 'red' : 'gray'}>
            Clients {report.clientCount}
            {report.maxClients != null ? ` / ${report.maxClients}` : ''}
          </Badge>
          <Badge tone="blue">{report.oversellPolicy}</Badge>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="py-1.5 pr-4 font-medium">Ceiling</th>
              <th className="py-1.5 pr-4 font-medium">Current</th>
              <th className="py-1.5 pr-4 font-medium">Limit</th>
              <th className="py-1.5 font-medium">Mode</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {report.lines.map((line) => (
              <tr key={line.key} className={line.exceeded ? 'text-red-600 dark:text-red-400' : ''}>
                <td className="py-1.5 pr-4">
                  {line.label}
                  {line.advisory && <span className="ml-1 text-xs text-slate-400">(advisory)</span>}
                </td>
                <td className="py-1.5 pr-4 tabular-nums">{line.aggregate}</td>
                <td className="py-1.5 pr-4 tabular-nums">{line.ceiling ?? '∞'}</td>
                <td className="py-1.5 text-xs text-slate-500">
                  {line.mode}
                  {line.availability ? ` · ${line.availability.replace('_', ' ')}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateResellerForm({ plans, onDone }: { plans: ResellerPlanDto[]; onDone: () => void }) {
  const create = useCreateReseller();
  const [values, setValues] = useState<CreateResellerInput>({
    name: '',
    email: '',
    resellerPlanId: plans[0]?.id ?? '',
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(values, { onSuccess: onDone });
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="r-name">Name</Label>
        <Input id="r-name" required value={values.name} onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))} />
      </div>
      <div>
        <Label htmlFor="r-email">Email</Label>
        <Input id="r-email" type="email" required value={values.email} onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))} />
      </div>
      <div>
        <Label htmlFor="r-plan">Reseller plan</Label>
        <Select id="r-plan" required value={values.resellerPlanId} onChange={(e) => setValues((v) => ({ ...v, resellerPlanId: e.target.value }))}>
          <option value="" disabled>
            Select a reseller plan…
          </option>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>
      {create.error != null && <ErrorNote error={create.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={create.isPending} disabled={!values.resellerPlanId}>
          Create reseller
        </Button>
      </div>
    </form>
  );
}

function ReportModal({ resellerId, onClose }: { resellerId: string; onClose: () => void }) {
  const report = useResellerReport(resellerId);
  return (
    <Modal title="Ceiling & capacity report" open onClose={onClose} wide>
      {report.isPending ? (
        <Spinner />
      ) : report.error ? (
        <ErrorNote error={report.error} />
      ) : (
        <ReportTable report={report.data} />
      )}
    </Modal>
  );
}

export function ResellersPage() {
  const [tab, setTab] = useState<'resellers' | 'plans'>('resellers');
  const navigate = useNavigate();
  const resellers = useResellers();
  const plans = useResellerPlans();
  const descriptors = useQuery({
    queryKey: ['plan-descriptors'],
    queryFn: () => api.get<PlanDescriptorsDto>('/api/plans/descriptors'),
  });
  const impersonate = useStartImpersonation();
  const removeReseller = useDeleteReseller();
  const removePlan = useDeleteResellerPlan();

  const [createResellerOpen, setCreateResellerOpen] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ResellerPlanDto | null>(null);
  const [reportFor, setReportFor] = useState<string | null>(null);

  const mutationError = removeReseller.error ?? removePlan.error ?? impersonate.error;

  return (
    <div>
      <PageHeader
        title="Resellers"
        description="The reseller tier — owned clients, plans and subscriptions governed by aggregate ceilings."
        actions={
          tab === 'resellers' ? (
            <Button onClick={() => setCreateResellerOpen(true)} disabled={(plans.data?.length ?? 0) === 0}>
              <Plus className="size-4" /> New reseller
            </Button>
          ) : (
            <Button onClick={() => setCreatePlanOpen(true)}>
              <Plus className="size-4" /> New reseller plan
            </Button>
          )
        }
      />

      <div className="mb-4">
        <Tabs
          tabs={[
            { key: 'resellers', label: 'Resellers' },
            { key: 'plans', label: 'Reseller plans' },
          ]}
          active={tab}
          onChange={(k) => setTab(k as 'resellers' | 'plans')}
        />
      </div>

      {mutationError != null && (
        <div className="mb-4">
          <ErrorNote error={mutationError} />
        </div>
      )}

      {tab === 'resellers' && (
        <Card>
          {resellers.isPending ? (
            <Spinner />
          ) : resellers.error ? (
            <div className="p-5">
              <ErrorNote error={resellers.error} />
            </div>
          ) : resellers.data.length === 0 ? (
            <EmptyState
              icon={<Network className="size-10" />}
              title="No resellers yet"
              description={
                (plans.data?.length ?? 0) === 0
                  ? 'Create a reseller plan first, then add a reseller.'
                  : 'Add a reseller to delegate a slice of clients and plans.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Reseller</th>
                    <th className="px-5 py-3 font-medium">Plan</th>
                    <th className="px-5 py-3 font-medium">Clients</th>
                    <th className="px-5 py-3 font-medium">Subscriptions</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {resellers.data.map((r: ResellerDto) => (
                    <tr key={r.id}>
                      <td className="px-5 py-3">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{r.email}</p>
                      </td>
                      <td className="px-5 py-3">
                        {r.resellerPlanName}
                        <Badge tone="gray" >{r.oversellPolicy}</Badge>
                      </td>
                      <td className="px-5 py-3 tabular-nums">{r.clientCount}</td>
                      <td className="px-5 py-3 tabular-nums">{r.subscriptionCount}</td>
                      <td className="px-5 py-3">
                        <Badge tone={r.status === 'active' ? 'green' : r.status === 'suspended' ? 'yellow' : 'gray'}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            variant="ghost"
                            onClick={() => setReportFor(r.id)}
                            aria-label={`Report for ${r.name}`}
                          >
                            <Gauge className="size-4" />
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() =>
                              impersonate.mutate(r.id, { onSuccess: () => navigate('/clients') })
                            }
                          >
                            <Eye className="size-4" /> View as
                          </Button>
                          <Button
                            variant="ghost"
                            className="hover:!text-red-600"
                            aria-label={`Delete ${r.name}`}
                            onClick={() => {
                              if (window.confirm(`Delete reseller ${r.name}?`)) removeReseller.mutate(r.id);
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'plans' && (
        <Card>
          {plans.isPending ? (
            <Spinner />
          ) : plans.error ? (
            <div className="p-5">
              <ErrorNote error={plans.error} />
            </div>
          ) : plans.data.length === 0 ? (
            <EmptyState icon={<Gauge className="size-10" />} title="No reseller plans" description="Define the ceilings a reseller operates within." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Plan</th>
                    <th className="px-5 py-3 font-medium">Max clients</th>
                    <th className="px-5 py-3 font-medium">Oversell</th>
                    <th className="px-5 py-3 font-medium">Resellers</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {plans.data.map((p) => (
                    <tr key={p.id}>
                      <td className="px-5 py-3 font-medium">{p.name}</td>
                      <td className="px-5 py-3 tabular-nums">{p.maxClients ?? '∞'}</td>
                      <td className="px-5 py-3">
                        <Badge tone="blue">{p.oversellPolicy}</Badge>
                      </td>
                      <td className="px-5 py-3 tabular-nums">{p.resellerCount}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" onClick={() => setEditingPlan(p)} aria-label={`Edit ${p.name}`}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="hover:!text-red-600"
                            aria-label={`Delete ${p.name}`}
                            onClick={() => {
                              if (window.confirm(`Delete reseller plan ${p.name}?`)) removePlan.mutate(p.id);
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Modal title="New reseller" open={createResellerOpen} onClose={() => setCreateResellerOpen(false)}>
        <CreateResellerForm plans={plans.data ?? []} onDone={() => setCreateResellerOpen(false)} />
      </Modal>

      {descriptors.data && createPlanOpen && (
        <Modal title="New reseller plan" open onClose={() => setCreatePlanOpen(false)} wide>
          <ResellerPlanForm descriptors={descriptors.data} onDone={() => setCreatePlanOpen(false)} />
        </Modal>
      )}

      {descriptors.data && editingPlan && (
        <Modal title={`Edit ${editingPlan.name}`} open onClose={() => setEditingPlan(null)} wide>
          <ResellerPlanForm descriptors={descriptors.data} initial={editingPlan} onDone={() => setEditingPlan(null)} />
        </Modal>
      )}

      {reportFor && <ReportModal resellerId={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}
